import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { LoginRequest, LoginResponse, User } from '../types';

// ── Single source of truth for the backend base URL ──────────────────────────
// The SPA is served statically (no nginx /api proxy); it must reach the backend
// cross-origin via REACT_APP_API_URL. `localhost:4000` is ONLY a dev default.
// If a *production* build ships without REACT_APP_API_URL set, the bundle would
// silently talk to the visitor's own machine — so we shout loudly at module load
// rather than fail mysteriously at runtime. (Audit finding #20.)
const DEV_FALLBACK_BASE_URL = 'http://localhost:4000';

export const IS_API_BASE_URL_CONFIGURED = Boolean(process.env.REACT_APP_API_URL);

export const API_BASE_URL = process.env.REACT_APP_API_URL || DEV_FALLBACK_BASE_URL;

if (!IS_API_BASE_URL_CONFIGURED && process.env.NODE_ENV === 'production') {
  // eslint-disable-next-line no-console
  console.error(
    '[api] REACT_APP_API_URL is NOT set in a production build. ' +
      `Falling back to ${DEV_FALLBACK_BASE_URL}, which points at the visitor's own ` +
      'machine and will not reach the backend. Set REACT_APP_API_URL at build time.',
  );
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response: AxiosResponse<LoginResponse> = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async getProfile(): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get('/auth/profile');
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/health');
    return response.data;
  }

  // Core HR APIs
  async getEntities(): Promise<any[]> {
    const response = await this.api.get('/api/entities');
    return response.data;
  }

  async getEmployee(id: string): Promise<any> {
    const response = await this.api.get(`/api/employees/${id}`);
    return response.data;
  }

  async getEmployees(entityId?: string): Promise<any[]> {
    const params = entityId ? { entityId } : {};
    const response = await this.api.get('/api/employees', { params });
    return response.data;
  }

  // Paginated variant. Returns { data, total, page, pageSize }. Defensive: if the
  // API responds with a plain array (legacy / un-paginated), it is normalised into
  // the same shape so callers can rely on a single contract.
  async getEmployeesPaged(
    opts: { page?: number; pageSize?: number; entityId?: string } = {},
  ): Promise<{ data: any[]; total: number; page: number; pageSize: number }> {
    const { page = 1, pageSize = 50, entityId } = opts;
    const params: Record<string, any> = { page, pageSize };
    if (entityId) params.entityId = entityId;
    const response = await this.api.get('/api/employees', { params });
    const body = response.data;
    if (Array.isArray(body)) {
      return { data: body, total: body.length, page, pageSize };
    }
    return {
      data: body?.data ?? [],
      total: body?.total ?? (body?.data?.length ?? 0),
      page: body?.page ?? page,
      pageSize: body?.pageSize ?? pageSize,
    };
  }

  async updateEmployee(employeeId: string, employeeData: any): Promise<any> {
    const response = await this.api.patch(`/api/employees/${employeeId}`, employeeData);
    return response.data;
  }

  async getDepartments(entityId?: string): Promise<any[]> {
    const params = entityId ? { entityId } : {};
    const response = await this.api.get('/api/departments', { params });
    return response.data;
  }

  async getPositions(entityId?: string, departmentId?: string): Promise<any[]> {
    const params: any = {};
    if (entityId) params.entityId = entityId;
    if (departmentId) params.departmentId = departmentId;
    const response = await this.api.get('/api/positions', { params });
    return response.data;
  }

  async createDepartment(departmentData: any): Promise<any> {
    const response = await this.api.post('/api/departments', departmentData);
    return response.data;
  }

  async updateDepartment(id: string, departmentData: any): Promise<any> {
    const response = await this.api.put(`/api/departments/${id}`, departmentData);
    return response.data;
  }

  async deleteDepartment(id: string): Promise<any> {
    const response = await this.api.delete(`/api/departments/${id}`);
    return response.data;
  }

  async createEntity(entityData: any): Promise<any> {
    const response = await this.api.post('/api/entities', entityData);
    return response.data;
  }

  async updateEntity(id: string, entityData: any): Promise<any> {
    const response = await this.api.patch(`/api/entities/${id}`, entityData);
    return response.data;
  }

  async deleteEntity(id: string): Promise<any> {
    const response = await this.api.delete(`/api/entities/${id}`);
    return response.data;
  }

  async createPosition(positionData: any): Promise<any> {
    const response = await this.api.post('/api/positions', positionData);
    return response.data;
  }

  async updatePosition(id: string, positionData: any): Promise<any> {
    const response = await this.api.patch(`/api/positions/${id}`, positionData);
    return response.data;
  }

  async deletePosition(id: string): Promise<any> {
    const response = await this.api.delete(`/api/positions/${id}`);
    return response.data;
  }

  // Countries
  async getCountries(): Promise<any[]> {
    const response = await this.api.get('/api/leave/countries');
    return response.data;
  }

  // Public Holidays
  async getPublicHolidays(year?: number, countryId?: number): Promise<any[]> {
    const params: any = {};
    if (year) params.year = year;
    if (countryId) params.countryId = countryId;
    const response = await this.api.get('/api/leave/holidays', { params });
    return response.data;
  }

  async createPublicHoliday(data: any): Promise<any> {
    const response = await this.api.post('/api/leave/holidays', data);
    return response.data;
  }

  async updatePublicHoliday(id: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/leave/holidays/${id}`, data);
    return response.data;
  }

  async deletePublicHoliday(id: string): Promise<any> {
    const response = await this.api.delete(`/api/leave/holidays/${id}`);
    return response.data;
  }

  // Leave APIs
  async getLeaveTypes(): Promise<any[]> {
    const response = await this.api.get('/api/leave/types');
    return response.data;
  }

  async getLeaveRequests(employeeId?: string): Promise<any[]> {
    const params = employeeId ? { employeeId } : {};
    const response = await this.api.get('/api/leave/requests', { params });
    return response.data;
  }

  async getMyLeaveBalances(year?: number): Promise<any[]> {
    const params = year ? { year } : {};
    const response = await this.api.get('/api/leave/balances/my-balances', { params });
    return response.data;
  }

  async getEmployeeLeaveBalances(employeeId: string, year?: number): Promise<any[]> {
    const params = year ? { year } : {};
    const response = await this.api.get(`/api/leave/balances/employee/${employeeId}`, { params });
    return response.data;
  }

  async initializeEmployeeLeaveBalances(employeeId: string, year?: number, customBalances?: Record<string, number>): Promise<any> {
    const params = year ? { year } : {};
    const body = customBalances ? { customBalances } : {};
    const response = await this.api.post(`/api/leave/balances/initialize/${employeeId}`, body, { params });
    return response.data;
  }

  async runAccrualEngine(year?: number): Promise<any> {
    const params = year ? { year } : {};
    const response = await this.api.post('/api/leave/balances/run-accrual', {}, { params });
    return response.data;
  }

  async runYearEndCarryOver(fromYear?: number): Promise<any> {
    const params = fromYear ? { fromYear } : {};
    const response = await this.api.post('/api/leave/balances/year-end-carry-over', {}, { params });
    return response.data;
  }

  async expireCarryOver(year?: number): Promise<any> {
    const params = year ? { year } : {};
    const response = await this.api.post('/api/leave/balances/expire-carry-over', {}, { params });
    return response.data;
  }

  async applyLeavePolicy(year?: number): Promise<any> {
    const params = year ? { year } : {};
    const response = await this.api.post('/api/leave/balances/apply-policy', {}, { params });
    return response.data;
  }

  async applyLeavePolicyToEmployee(employeeId: string, year?: number): Promise<any> {
    const params = year ? { year } : {};
    const response = await this.api.post(`/api/leave/balances/apply-policy/${employeeId}`, {}, { params });
    return response.data;
  }

  async adjustEmployeeLeaveBalance(employeeId: string, leaveTypeId: string, adjustmentDays: number, reason: string, year?: number): Promise<any> {
    const response = await this.api.post(`/api/leave/balances/adjust`, {
      employeeId,
      leaveTypeId,
      adjustmentDays,
      reason,
      year: year || new Date().getFullYear()
    });
    return response.data;
  }

  async submitLeaveRequest(request: any): Promise<any> {
    const response = await this.api.post('/api/leave/requests', request);
    return response.data;
  }

  async withdrawLeaveRequest(id: string): Promise<any> {
    const response = await this.api.patch(`/api/leave/requests/${id}/withdraw`);
    return response.data;
  }

  async getPendingLeaveForApprover(): Promise<any[]> {
    const response = await this.api.get('/api/leave/requests/pending');
    return response.data;
  }

  async approveLeaveRequest(requestId: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/leave/requests/${requestId}/approve`, data);
    return response.data;
  }

  async cancelLeaveRequest(requestId: string): Promise<any> {
    const response = await this.api.patch(`/api/leave/requests/${requestId}/cancel`);
    return response.data;
  }

  // Payroll APIs
  async getPayrollRuns(entityId?: string): Promise<any[]> {
    const params = entityId ? { entityId } : {};
    const response = await this.api.get('/api/payroll/runs', { params });
    return response.data;
  }

  async getPayrollAdjustments(entityId?: string): Promise<any[]> {
    const params = entityId ? { entityId } : {};
    const response = await this.api.get('/api/payroll/adjustments', { params });
    return response.data;
  }

  // Recruitment APIs
  async getRequisitions(entityId?: string): Promise<any[]> {
    const params = entityId ? { entityId } : {};
    const response = await this.api.get('/api/recruitment/requisitions', { params });
    return response.data;
  }

  async getRequisition(id: string): Promise<any> {
    const response = await this.api.get(`/api/recruitment/requisitions/${id}`);
    return response.data;
  }

  async submitRequisitionForApproval(id: string): Promise<any> {
    const response = await this.api.patch(`/api/recruitment/requisitions/${id}/submit`);
    return response.data;
  }

  async getPendingRequisitionApprovals(): Promise<any[]> {
    const response = await this.api.get('/api/recruitment/requisitions/pending-approvals');
    return response.data;
  }

  async approveRequisition(id: string, comments?: string): Promise<any> {
    const response = await this.api.patch(`/api/recruitment/requisitions/${id}/approve`, { comments });
    return response.data;
  }

  async rejectRequisition(id: string, comments?: string): Promise<any> {
    const response = await this.api.patch(`/api/recruitment/requisitions/${id}/reject`, { comments });
    return response.data;
  }


  async getCandidates(search?: string): Promise<any[]> {
    const params = search ? { search } : {};
    const response = await this.api.get('/api/recruitment/candidates', { params });
    return response.data;
  }

  async getRecruitmentPipeline(requisitionId?: string): Promise<any> {
    const params = requisitionId ? { requisitionId } : {};
    const response = await this.api.get('/api/recruitment/candidates/pipeline/overview', { params });
    return response.data;
  }

  // Performance APIs
  async getPerformanceCycles(entityId?: string): Promise<any[]> {
    const params = entityId ? { entityId } : {};
    const response = await this.api.get('/api/performance/cycles', { params });
    return response.data;
  }

  async getMyReviews(cycleId?: string): Promise<any[]> {
    const params = cycleId ? { cycleId } : {};
    const response = await this.api.get('/api/performance/reviews/my-reviews', { params });
    return response.data;
  }

  async getActiveProbations(): Promise<any[]> {
    const response = await this.api.get('/api/performance/probation/active');
    return response.data;
  }

  // Activity APIs
  async getRecentActivities(limit?: number, entityId?: string): Promise<any[]> {
    const params: any = {};
    if (limit) params.limit = limit;
    if (entityId) params.entityId = entityId;
    const response = await this.api.get('/api/activities/recent', { params });
    return response.data;
  }

  async getMyActivities(limit?: number): Promise<any[]> {
    const params = limit ? { limit } : {};
    const response = await this.api.get('/api/activities/my-activities', { params });
    return response.data;
  }


  async getAuditLogs(options: { limit?: number; offset?: number; tableName?: string; action?: string; userId?: string; recordId?: string } = {}): Promise<{ data: any[]; total: number }> {
    const response = await this.api.get('/api/audit-logs', { params: options });
    return response.data;
  }

  async createPayrollRun(payrollData: any): Promise<any> {
    const response = await this.api.post('/api/payroll/runs', payrollData);
    return response.data;
  }

  // User Management APIs
  async getUsers(): Promise<any[]> {
    const response = await this.api.get('/auth/users');
    return response.data;
  }

  async getAccessControlView(): Promise<any[]> {
    const response = await this.api.get('/auth/access-control');
    return response.data;
  }

  async createUser(userData: any): Promise<any> {
    const response = await this.api.post('/auth/users', userData);
    return response.data;
  }

  async updateUser(userId: string, userData: any): Promise<any> {
    const response = await this.api.put(`/auth/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: string): Promise<any> {
    const response = await this.api.delete(`/auth/users/${userId}`);
    return response.data;
  }

  async forceChangePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.post('/auth/force-change-password', { currentPassword, newPassword });
  }

  async firstLogin(email: string, password: string): Promise<any> {
    const response = await this.api.post('/auth/first-login', { email, password });
    return response.data;
  }

  async acceptInvite(token: string, password: string): Promise<any> {
    const response = await this.api.post('/auth/accept-invite', { token, password });
    return response.data;
  }

  async forgotPassword(email: string): Promise<void> {
    await this.api.post('/auth/forgot-password', { email });
  }

  async resetPassword(email: string, newPassword: string): Promise<void> {
    await this.api.post('/auth/reset-password', { email, newPassword });
  }

  async resetPasswordWithToken(token: string, newPassword: string): Promise<void> {
    await this.api.post('/auth/reset-password', { token, newPassword });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.put('/auth/change-password', { currentPassword, newPassword });
  }

  async resetUserPassword(userId: string): Promise<any> {
    const response = await this.api.post(`/auth/users/${userId}/reset-password`);
    return response.data;
  }

  async updateMyProfile(data: any): Promise<any> {
    const response = await this.api.patch('/api/employees/me', data);
    return response.data;
  }

  async createEmployee(data: any): Promise<any> {
    const response = await this.api.post('/api/employees', data);
    return response.data;
  }

  async importEmployees(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/api/employees/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async createRequisition(data: any): Promise<any> {
    const response = await this.api.post('/api/recruitment/requisitions', data);
    return response.data;
  }

  async createCandidate(data: any): Promise<any> {
    const response = await this.api.post('/api/recruitment/candidates', data);
    return response.data;
  }

  async createPerformanceCycle(data: any): Promise<any> {
    const response = await this.api.post('/api/performance/cycles', data);
    return response.data;
  }

  async resendInvite(userId: string): Promise<{ message: string; inviteUrl: string }> {
    const response = await this.api.post(`/auth/users/${userId}/resend-invite`);
    return response.data;
  }

  async sendInviteForEmployee(employeeId: string): Promise<{ message: string; inviteUrl: string }> {
    const response = await this.api.post(`/auth/employees/${employeeId}/send-invite`);
    return response.data;
  }
  // Onboarding APIs
  async getOnboardingTemplates(): Promise<any[]> {
    const response = await this.api.get('/api/onboarding/templates');
    return response.data;
  }

  async createOnboardingTemplate(data: any): Promise<any> {
    const response = await this.api.post('/api/onboarding/templates', data);
    return response.data;
  }

  async getEmployeeOnboardings(): Promise<any[]> {
    const response = await this.api.get('/api/onboarding');
    return response.data;
  }

  async createEmployeeOnboarding(data: any): Promise<any> {
    const response = await this.api.post('/api/onboarding', data);
    return response.data;
  }

  async updateOnboardingTask(taskId: string, data: { status: string; notes?: string }): Promise<any> {
    const response = await this.api.patch(`/api/onboarding/tasks/${taskId}`, data);
    return response.data;
  }
  // Document APIs
  async getEmployeeDocuments(employeeId: string): Promise<any[]> {
    const response = await this.api.get(`/api/documents/employee/${employeeId}`);
    return response.data;
  }

  async uploadDocument(employeeId: string, file: File, category: string, description: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    formData.append('description', description);
    const response = await this.api.post(`/api/documents/employee/${employeeId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteDocument(documentId: string): Promise<any> {
    const response = await this.api.delete(`/api/documents/${documentId}`);
    return response.data;
  }

  getDocumentDownloadUrl(documentId: string): string {
    // Absolute URL against the single backend base. There is NO nginx /api
    // proxy — the SPA is served statically and must reach the backend
    // cross-origin (audit #12), so a relative path here would hit the static
    // site and return HTML instead of the file.
    return `${API_BASE_URL.replace(/\/$/, '')}/api/documents/${documentId}/download`;
  }
  // Business Trips
  async getBusinessTrips(): Promise<any[]> {
    const response = await this.api.get('/api/business-trips');
    return response.data;
  }

  async createBusinessTrip(data: any): Promise<any> {
    const response = await this.api.post('/api/business-trips', data);
    return response.data;
  }

  async approveBusinessTrip(id: string, data: { action: string; rejectionReason?: string }): Promise<any> {
    const response = await this.api.patch(`/api/business-trips/${id}/approve`, data);
    return response.data;
  }

  async cancelBusinessTrip(id: string): Promise<any> {
    const response = await this.api.patch(`/api/business-trips/${id}/cancel`);
    return response.data;
  }

  // Legacy per-employee contract endpoints (employee profile sub-resource)
  async getEmployeeLegacyContracts(employeeId: string): Promise<any[]> {
    const response = await this.api.get(`/api/employees/${employeeId}/contracts`);
    return response.data;
  }

  async createEmployeeLegacyContract(employeeId: string, data: any): Promise<any> {
    const response = await this.api.post(`/api/employees/${employeeId}/contracts`, data);
    return response.data;
  }

  async updateEmployeeLegacyContract(employeeId: string, contractId: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/employees/${employeeId}/contracts/${contractId}`, data);
    return response.data;
  }

  async getEmployeeCompensation(employeeId: string): Promise<any[]> {
    const response = await this.api.get(`/api/employees/${employeeId}/compensation`);
    return response.data;
  }

  async createEmployeeCompensation(employeeId: string, data: any): Promise<any> {
    const response = await this.api.post(`/api/employees/${employeeId}/compensation`, data);
    return response.data;
  }

  async updateEmployeeCompensation(employeeId: string, compId: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/employees/${employeeId}/compensation/${compId}`, data);
    return response.data;
  }

  // ── Offboarding ─────────────────────────────────────────────────────────────

  async getOffboardingTemplates(): Promise<any[]> {
    const response = await this.api.get('/api/offboarding/templates');
    return response.data;
  }

  async createOffboardingTemplate(data: any): Promise<any> {
    const response = await this.api.post('/api/offboarding/templates', data);
    return response.data;
  }

  async updateOffboardingTemplate(id: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/offboarding/templates/${id}`, data);
    return response.data;
  }

  async deleteOffboardingTemplate(id: string): Promise<void> {
    await this.api.delete(`/api/offboarding/templates/${id}`);
  }

  async getAllOffboardings(): Promise<any[]> {
    const response = await this.api.get('/api/offboarding');
    return response.data;
  }

  async getOffboarding(id: string): Promise<any> {
    const response = await this.api.get(`/api/offboarding/${id}`);
    return response.data;
  }

  async getEmployeeOffboarding(employeeId: string): Promise<any[]> {
    const response = await this.api.get(`/api/offboarding/employee/${employeeId}`);
    return response.data;
  }

  async createOffboarding(data: any): Promise<any> {
    const response = await this.api.post('/api/offboarding', data);
    return response.data;
  }

  async updateOffboarding(id: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/offboarding/${id}`, data);
    return response.data;
  }

  async updateOffboardingTask(offboardingId: string, taskId: string, data: { status?: string; notes?: string }): Promise<any> {
    const response = await this.api.patch(`/api/offboarding/${offboardingId}/tasks/${taskId}`, data);
    return response.data;
  }

  async completeOffboarding(id: string): Promise<any> {
    const response = await this.api.patch(`/api/offboarding/${id}/complete`);
    return response.data;
  }

  async cancelOffboarding(id: string): Promise<any> {
    const response = await this.api.patch(`/api/offboarding/${id}/cancel`);
    return response.data;
  }

  async deleteOffboarding(id: string): Promise<void> {
    await this.api.delete(`/api/offboarding/${id}`);
  }

  // ── Training & Development ──────────────────────────────────────────────────

  async getTrainingCourses(activeOnly = false): Promise<any[]> {
    const response = await this.api.get('/api/training/courses', { params: activeOnly ? { activeOnly: 'true' } : {} });
    return response.data;
  }

  async createTrainingCourse(data: any): Promise<any> {
    const response = await this.api.post('/api/training/courses', data);
    return response.data;
  }

  async updateTrainingCourse(id: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/training/courses/${id}`, data);
    return response.data;
  }

  async deleteTrainingCourse(id: string): Promise<void> {
    await this.api.delete(`/api/training/courses/${id}`);
  }

  async getAllTrainingRecords(): Promise<any[]> {
    const response = await this.api.get('/api/training/records');
    return response.data;
  }

  async getTrainingStats(): Promise<any> {
    const response = await this.api.get('/api/training/records/stats');
    return response.data;
  }

  async getExpiringSoonTraining(days = 30): Promise<any[]> {
    const response = await this.api.get('/api/training/records/expiring-soon', { params: { days } });
    return response.data;
  }

  async getOverdueTraining(): Promise<any[]> {
    const response = await this.api.get('/api/training/records/overdue');
    return response.data;
  }

  async getEmployeeTrainingRecords(employeeId: string): Promise<any[]> {
    const response = await this.api.get(`/api/training/employee/${employeeId}/records`);
    return response.data;
  }

  async createTrainingRecord(data: any): Promise<any> {
    const response = await this.api.post('/api/training/records', data);
    return response.data;
  }

  async updateTrainingRecord(id: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/training/records/${id}`, data);
    return response.data;
  }

  async deleteTrainingRecord(id: string): Promise<void> {
    await this.api.delete(`/api/training/records/${id}`);
  }

  // ── Asset Management ────────────────────────────────────────────────────────

  async getAssets(): Promise<any[]> {
    const response = await this.api.get('/api/assets');
    return response.data;
  }

  async getAssetStats(): Promise<any> {
    const response = await this.api.get('/api/assets/stats');
    return response.data;
  }

  async getAllAssetAssignments(): Promise<any[]> {
    const response = await this.api.get('/api/assets/assignments');
    return response.data;
  }

  async getEmployeeAssets(employeeId: string): Promise<any[]> {
    const response = await this.api.get(`/api/assets/employee/${employeeId}`);
    return response.data;
  }

  async createAsset(data: any): Promise<any> {
    const response = await this.api.post('/api/assets', data);
    return response.data;
  }

  async updateAsset(id: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/assets/${id}`, data);
    return response.data;
  }

  async deleteAsset(id: string): Promise<void> {
    await this.api.delete(`/api/assets/${id}`);
  }

  async assignAsset(assetId: string, data: any): Promise<any> {
    const response = await this.api.post(`/api/assets/${assetId}/assign`, data);
    return response.data;
  }

  async returnAsset(assetId: string, assignmentId: string, data?: any): Promise<any> {
    const response = await this.api.patch(`/api/assets/${assetId}/assignments/${assignmentId}/return`, data || {});
    return response.data;
  }

  // ── Contracts ───────────────────────────────────────────────────────────────

  async getContracts(): Promise<any[]> {
    const response = await this.api.get('/api/contracts');
    return response.data;
  }

  async getContractStats(): Promise<any> {
    const response = await this.api.get('/api/contracts/stats');
    return response.data;
  }

  async getExpiringContracts(days = 60): Promise<any[]> {
    const response = await this.api.get('/api/contracts/expiring', { params: { days } });
    return response.data;
  }

  async getExpiredContracts(): Promise<any[]> {
    const response = await this.api.get('/api/contracts/expired');
    return response.data;
  }

  async getProbationEndingSoon(days = 30): Promise<any[]> {
    const response = await this.api.get('/api/contracts/probation', { params: { days } });
    return response.data;
  }

  async getEmployeeContracts(employeeId: string): Promise<any[]> {
    const response = await this.api.get(`/api/contracts/employee/${employeeId}`);
    return response.data;
  }

  async createContract(data: any): Promise<any> {
    const response = await this.api.post('/api/contracts', data);
    return response.data;
  }

  async updateContract(id: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/contracts/${id}`, data);
    return response.data;
  }

  async renewContract(id: string, data: any): Promise<any> {
    const response = await this.api.post(`/api/contracts/${id}/renew`, data);
    return response.data;
  }

  async deleteContract(id: string): Promise<void> {
    await this.api.delete(`/api/contracts/${id}`);
  }

  // ── Expense Claims ──────────────────────────────────────────────────────────

  async getAllExpenses(): Promise<any[]> {
    const response = await this.api.get('/api/expenses');
    return response.data;
  }

  async getExpenseStats(): Promise<any> {
    const response = await this.api.get('/api/expenses/stats');
    return response.data;
  }

  async getPendingExpenses(): Promise<any[]> {
    const response = await this.api.get('/api/expenses/pending');
    return response.data;
  }

  async getEmployeeExpenses(employeeId: string): Promise<any[]> {
    const response = await this.api.get(`/api/expenses/employee/${employeeId}`);
    return response.data;
  }

  async getExpense(id: string): Promise<any> {
    const response = await this.api.get(`/api/expenses/${id}`);
    return response.data;
  }

  async createExpenseClaim(data: any): Promise<any> {
    const response = await this.api.post('/api/expenses', data);
    return response.data;
  }

  async updateExpenseClaim(id: string, data: any): Promise<any> {
    const response = await this.api.patch(`/api/expenses/${id}`, data);
    return response.data;
  }

  async submitExpenseClaim(id: string): Promise<any> {
    const response = await this.api.patch(`/api/expenses/${id}/submit`);
    return response.data;
  }

  async processExpenseApproval(id: string, data: { action: string; approvedById?: string; rejectionReason?: string }): Promise<any> {
    const response = await this.api.patch(`/api/expenses/${id}/approval`, data);
    return response.data;
  }

  async deleteExpenseClaim(id: string): Promise<void> {
    await this.api.delete(`/api/expenses/${id}`);
  }

}

export const apiService = new ApiService();
export default apiService;